// Import classnames utility for conditional CSS class application
import classNames from "classnames";
// Import React hooks for performance optimization and memoization
import { useCallback, useMemo, useState } from "react";
// Import i18n translation hook for internationalization
import { useTranslation } from "react-i18next";
// Import component-specific styles
import styles from "./styles.module.scss";

// helpers

// types
import {
  TransactionAction,
  TransactionHistory,
  TransactionStatus,
  TransactionType,
  WalletHistoryRaw,
  WalletTransaction,
} from "../types";

// components
import { getArabicOrdinalFemale } from "../../../../setup/util/arabicOrdinalName";
import { formatDate, formatTime } from "../../../../setup/util/wallet";
import BalanceDetailsModal from "../balanceDetailsModal";

export interface WalletListItemProps {
  dash?: boolean;
  hideWithdrawTab?: boolean;
  transaction: WalletTransaction;
  showMobileView?: boolean;
  onClick?: () => void;
  isMobileModal?: boolean;
  milestoneIndex?: number;
}

const WalletListItem = ({
  hideWithdrawTab,
  dash = false,
  transaction,
  showMobileView = false,
  onClick,
  isMobileModal = false,
  milestoneIndex,
}: WalletListItemProps) => {
  const { t } = useTranslation();
  const [currentTimelineItems, setCurrentTimelineItems] = useState<
    WalletHistoryRaw[]
  >([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<
    "customer_pay" | "settle" | "cashout" | "org_cashout"
  >("customer_pay");

  const { icon, statusColor } = useMemo(() => {
    switch (transaction.status) {
      case TransactionStatus.OPENED:
        return {
          icon: "assets/images/svg/light_blue_clock.svg",
          statusColor: "blue",
        };
      case TransactionStatus.IN_PROGRESS:
        return {
          icon: "assets/images/svg/light_yellow_rewind.svg",
          statusColor: "yellow",
        };
      case TransactionStatus.COMPLETED:
        return {
          icon: "assets/images/svg/light_green_tick.svg",
          statusColor: "green",
        };
      default:
        return {
          icon: "assets/images/svg/light_blue_clock.svg",
          statusColor: "green",
        };
    }
  }, [transaction.status]);

  const getTransactionStatusText = useCallback(() => {
    switch (transaction.status) {
      case TransactionStatus.OPENED:
        return `${t("balance_transaction_status_open")} ...`;
      case TransactionStatus.IN_PROGRESS:
        return `${t("balance_transaction_status_processing")} ...`;
      case TransactionStatus.COMPLETED:
        return t("balance_transaction_status_transferred");
      default:
        return "";
    }
  }, [transaction.status, t]);

  const getTransactionStatus = useCallback(() => {
    switch (transaction.status) {
      case TransactionStatus.OPENED:
        return `${t("balance_transaction_status_open")}`;
      case TransactionStatus.IN_PROGRESS:
        return `${t("balance_number_card_processing_title")}`;
      case TransactionStatus.COMPLETED:
        return `${t("balance_transaction_status_completed")}`;
      default:
        return "";
    }
  }, [transaction.status, t]);

  const getTransactionTitle = useCallback(() => {
    return transaction.title || "-";
  }, [transaction.title]);

  const getTransactionSubtitle = useCallback(
    (index) => {
      return `${t("lbl_milestone")} ${getArabicOrdinalFemale(index + 1)} - `;
    },
    [t]
  );

  const formatAmount = useCallback(
    (amount: number) => {
      return `${amount} ${t("lbl_saudi_currency")}`;
    },
    [t]
  );

  const getCustomerPayStepDescription = useCallback(() => {
    const description = t("balance_title_payment");
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentTimelineItems(transaction?.summarizedByAction?.ADD);
      setModalType("customer_pay");
      setModalOpen(true);
    };

    // Split the text to find "التسوية" and make it clickable
    const parts = description.split("عرض التفاصيل");
    if (parts.length > 1) {
      return (
        <>
          {parts[0]}

          <span
            onClick={handleClick}
            style={{
              textDecoration: "underline",
              cursor: "pointer",
            }}>
            عرض التفاصيل
          </span>

          {parts[1]}
        </>
      );
    }
  }, [t, transaction?.summarizedByAction?.ADD]);

  const getSettlementStepDescription = useCallback(
    (hasSettlement: boolean) => {
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentTimelineItems(
          transaction?.summarizedByAction?.SETTLEMENT_INITIATED
        );
        if (hasSettlement) {
          setModalType("settle");
          setModalOpen(true);
        }
      };

      const description = t("balance_title_settlement");

      // Split the text to find "التسوية" and make it clickable
      const parts = description.split("قرار التوزيع");
      if (parts.length > 1) {
        return (
          <>
            {parts[0]}
            {hasSettlement && (
              <span
                onClick={handleClick}
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}>
                قرار التوزيع
              </span>
            )}
            {parts[1]}
          </>
        );
      }
    },
    [t, transaction?.summarizedByAction?.SETTLEMENT_INITIATED]
  );

  const getCashedOutStepDescription = useCallback(() => {
    const description = t("balance_title_cashout");
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentTimelineItems([
        ...(transaction?.summarizedByAction?.MANUAL_WITHDRAWAL_INITIATED || []),
        ...(transaction?.summarizedByAction?.SUBTRACT || []),
      ]);
      setModalType("cashout"); // TODO: When org cashout?
      setModalOpen(true);
    };

    // Split the text to find "التسوية" and make it clickable
    const parts = description.split("عرض التفاصيل");
    if (parts.length > 1) {
      return (
        <>
          {parts[0]}
          {hideWithdrawTab && (
            <span
              onClick={handleClick}
              style={{
                textDecoration: "underline",
                cursor: "pointer",
              }}>
              عرض التفاصيل
            </span>
          )}
          {parts[1]}
        </>
      );
    }
  }, [
    hideWithdrawTab,
    t,
    transaction?.summarizedByAction?.MANUAL_WITHDRAWAL_INITIATED,
    transaction?.summarizedByAction?.SUBTRACT,
  ]);

  const getDescription = useCallback(
    (history: TransactionHistory) => {
      const isCustomerPay = history.type === TransactionType.CUSTOMER_PAY;
      const isAddAction = history.action === TransactionAction.ADD;
      const isCustomerPayStep = isCustomerPay && isAddAction;

      // const hasSettlement = history.amount > 0; can be negative
      const isSettlement = history.type === TransactionType.SETTLEMENT;
      const isSettlementInitiatedAction =
        history.action === TransactionAction.SETTLEMENT_INITIATED;
      const isSettlementStep = isSettlement && isSettlementInitiatedAction;

      const isReleaseToProvider =
        history.type === TransactionType.RELEASE_TO_PROVIDER;
      const isCloseReleaseToProvider =
        history.type === TransactionType.CLOSE_RELEASE_TO_PROVIDER;
      const isRefundToCustomer =
        history.type === TransactionType.REFUND_CUSTOMER;
      const isSubtractAction = history.action === TransactionAction.SUBTRACT;

      const isCashedOutStep =
        isSubtractAction &&
        (isReleaseToProvider || isCloseReleaseToProvider || isRefundToCustomer);

      // New logic for manual withdrawal
      const isManualWithdrawal =
        history.type === TransactionType.MANUAL_WITHDRAWAL;
      const isManualWithdrawalInitiated =
        history.action === TransactionAction.MANUAL_WITHDRAWAL_INITIATED;
      const isManualWithdrawalStep =
        isManualWithdrawal && isManualWithdrawalInitiated;

      if (isCustomerPayStep) {
        return getCustomerPayStepDescription();
      }
      if (isSettlementStep) {
        return getSettlementStepDescription(true);
      }
      if (isManualWithdrawalStep) {
        return t("balance_useller_timeline_bulk_transfered");
      }
      if (isCashedOutStep) {
        return getCashedOutStepDescription();
      }

      return getCustomerPayStepDescription();
    },
    [
      getCustomerPayStepDescription,
      getSettlementStepDescription,
      getCashedOutStepDescription,
      t,
    ]
  );

  const renderTimelineItem = useCallback(
    (
      history: TransactionHistory,
      currentAmount: number,
      totalAmount: number
    ) => {
      const message = getDescription(history);
      const date = formatDate(history.date);
      const time = formatTime(history.date);

      const isCustomerPay = history.type === TransactionType.CUSTOMER_PAY;
      const isAddAction = history.action === TransactionAction.ADD;
      const isCustomerPayStep = isCustomerPay && isAddAction;

      const hasSettlement = history.amount > 0;
      const isSettlement = history.type === TransactionType.SETTLEMENT;
      const isSettlementInitiatedAction =
        history.action === TransactionAction.SETTLEMENT_INITIATED;
      const isSettlementStep =
        hasSettlement && isSettlement && isSettlementInitiatedAction;

      const isReleaseToProvider =
        history.type === TransactionType.RELEASE_TO_PROVIDER;
      const isCloseReleaseToProvider =
        history.type === TransactionType.CLOSE_RELEASE_TO_PROVIDER;
      const isRefundToCustomer =
        history.type === TransactionType.REFUND_CUSTOMER;
      const isSubtractAction = history.action === TransactionAction.SUBTRACT;
      const isCashedOutStep =
        isSubtractAction &&
        (isReleaseToProvider || isCloseReleaseToProvider || isRefundToCustomer);

      // New logic for showing amount
      const showAmount = true;

      // If amount is missing or falsy, show 0
      const displayAmount = history.amount ? history.amount : 0;
      return (
        <div className={styles["item_row"]}>
          <div className={classNames(styles["item_container"])}>
            <div className={styles["item_title"]}>
              <p className={isMobileModal ? styles["f18"] : ""}>{message}</p>
              {isCustomerPayStep && currentAmount && totalAmount && (
                <div className={styles.barWrapper}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                    <p className={styles["bold_price"]}>
                      {formatAmount(+currentAmount.toFixed(1))}
                    </p>
                    <p className={styles["light_price"]}>
                      {formatAmount(+totalAmount.toFixed(1))}
                    </p>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{
                        width: `${(currentAmount / totalAmount) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              <div className={styles["thread"]} />
            </div>
            <div
              className={classNames(
                styles["details_container"],
                isMobileModal && styles["mt_8"]
              )}>
              {/* // for settlement and cashout */}
              {((isSettlementStep && !hideWithdrawTab) ||
                (isCashedOutStep && hideWithdrawTab)) && (
                <p className={styles["price"]}>{formatAmount(displayAmount)}</p>
              )}
              {/* // for cashout only */}
              {history?.date && isCashedOutStep && (
                <>
                  <div className={styles["data_cont"]}>
                    {showAmount && <div className={styles["dot"]} />}

                    <p
                      className={classNames(
                        styles["time"],
                        isMobileModal && styles["f12"]
                      )}>
                      {date}
                    </p>
                  </div>
                  <div className={styles["time_cont"]}>
                    <img
                      src="assets/images/svg/hollow-clock-circle.svg"
                      className={styles["clock_icon"]}
                      alt="clock"
                    />
                    <p
                      className={classNames(
                        styles["time"],
                        isMobileModal && styles["f12"]
                      )}>
                      {time}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
    [getDescription, isMobileModal, hideWithdrawTab, formatAmount]
  );

  // Calculate the total sum of ADD amounts using reduce
  const calculateTotalAddAmount = useCallback(() => {
    if (
      !transaction?.summarizedByAction?.ADD ||
      transaction?.summarizedByAction?.ADD.length === 0
    ) {
      return 0;
    }

    return transaction.summarizedByAction.ADD.reduce((sum, item) => {
      return sum + (item?.amount || 0);
    }, 0);
  }, [transaction?.summarizedByAction?.ADD]);

  const renderTimelineItems = useCallback(() => {
    const timelineItems = transaction.walletHistories;
    const totalAddAmount = calculateTotalAddAmount();

    // Always render all items for desktop view
    if (!showMobileView) {
      return timelineItems.map((history, index) => (
        <div key={`${history.domainId}-${index}`}>
          {renderTimelineItem(
            history,
            totalAddAmount,
            transaction?.totalAmount
          )}
        </div>
      ));
    }

    // For mobile view, show limited items
    if (timelineItems.length <= 1) {
      return timelineItems.map((history, index) => (
        <div key={`${history.domainId}-${index}`}>
          {renderTimelineItem(
            history,
            totalAddAmount,
            transaction?.totalAmount
          )}
        </div>
      ));
    }

    // For mobile view with multiple items, show dots + last item
    return (
      <>
        <div className={styles["item_row"]} key="dots">
          <div className={styles["item_container"]}>
            <div className={styles["item_title"]}>
              <p className={styles["dots"]}>.....</p>
              <div className={styles["thread"]} />
            </div>
          </div>
        </div>
        <div key={`${timelineItems[timelineItems.length - 1].domainId}-last`}>
          {renderTimelineItem(
            timelineItems[timelineItems.length - 1],
            totalAddAmount,
            transaction?.totalAmount
          )}
        </div>
      </>
    );
  }, [
    transaction.walletHistories,
    transaction?.totalAmount,
    showMobileView,
    renderTimelineItem,
    calculateTotalAddAmount,
  ]);

  return (
    <>
      {dash && <div className={styles["dash_line"]} />}
      <div className={classNames(styles["outer_container"])}>
        {isMobileModal && (
          <div className={styles["icon_cont"]}>
            <img src={icon} alt="wallet" className={styles["icon"]} />
            <h1 className={styles["icon_status"]}>{getTransactionStatus()}</h1>

            <div
              className={classNames(styles["header_container"], {
                [styles["no_gap"]]: getTransactionTitle() === "",
              })}>
              {transaction?.milestoneMetadata?.order && (
                <p className={styles["title"]}>
                  {getTransactionSubtitle(
                    transaction?.milestoneMetadata?.order - 1
                  )}
                </p>
              )}
              <p className={styles["extra"]}>{getTransactionTitle()}</p>
            </div>
          </div>
        )}
        <div
          className={classNames(styles["container"], {
            [styles["mobileShadow"]]: showMobileView,
          })}
          onClick={onClick}
          style={onClick ? { cursor: "pointer" } : undefined}>
          <div
            className={classNames(
              styles["right_section"],
              isMobileModal && styles["right_2"]
            )}>
            {!isMobileModal && <img src={icon} alt="wallet" />}
            <div className={styles["line"]} />
          </div>

          <div className={styles["left_section"]}>
            <div>
              {!isMobileModal && (
                <div className={styles["title_cont"]}>
                  {transaction?.milestoneMetadata?.order && (
                    <p className={styles["title"]}>
                      {getTransactionSubtitle(
                        transaction?.milestoneMetadata?.order - 1
                      )}
                    </p>
                  )}
                  <p className={styles["extra"]}>{getTransactionTitle()}</p>
                </div>
              )}

              <div className={styles["outter_container"]}>
                <div className={styles["line_container"]} />
                <div
                  className={styles["item_list"]}
                  style={
                    showMobileView && !isMobileModal ? { gap: "0.5rem" } : {}
                  }>
                  {renderTimelineItems()}

                  <div className={styles["item_row"]}>
                    <div className={styles["thread"]} />
                    <p
                      className={classNames(
                        styles["status_text"],
                        styles[statusColor],
                        isMobileModal && styles["f14"]
                      )}>
                      {getTransactionStatusText()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {!isMobileModal && (
              <img
                className={styles["arrow_icon"]}
                src="assets/images/svg/arrow_left_thinner.svg"
                alt="arrow"
              />
            )}
          </div>
        </div>
      </div>

      {currentTimelineItems?.filter((item) => item?.amount !== 0)?.length !==
        0 && (
        <BalanceDetailsModal
          hideWithdrawTab={!!hideWithdrawTab}
          {...(modalType === "customer_pay"
            ? {
                title: t("balance_pay_title"),
                type: "customer_pay",
              }
            : modalType === "settle"
            ? {
                title: t("balance_settle_title"),
                type: "settle",
              }
            : {
                title: t("balance_cashout_title"),
                type: "cashout",
              })}
          timelineItems={currentTimelineItems?.filter(
            (item) => !!item?.amount && item?.amount !== 0
          )}
          milestoneTitle={`${getTransactionTitle()}`}
          handleClose={() => setModalOpen(false)}
          modalOpen={modalOpen}
        />
      )}
    </>
  );
};

export default WalletListItem;
